import type {
  ResultFileApi,
  ResultRead,
  SwitchingWindowCalculated,
  SwitchingWindowRead,
} from '@gridcon-energy/schaltungstool-backend'
import { ResultFileType } from '@gridcon-energy/schaltungstool-backend'
import type { MethodNames } from '~/types/helpers'
import * as Papa from 'papaparse'

/**
 * Timeline chart data structure
 */
export interface ResultChartData {
  result: ResultRead
  xAxisData: number[]
  series: EquipmentSeries[]
}

/**
 * Individual series in the timeline chart
 */
export interface EquipmentSeries {
  name: string
  uniqueId: string // Unique identifier: resultId_seriesName
  data: number[]
  color: string
  visible: boolean
}

/**
 * View mode for displaying data
 */
export enum ViewModeEnum {
  max = 'max', // default
  individual = 'individual',
}

/**
 * Calculated switching window for a single result
 */
export type SwitchingWindowCalculation = {
  result: ResultRead
  switchingWindow: SwitchingWindowRead
  windows: Array<SwitchingWindowCalculated>
  visible: boolean
}
export type SwitchingWindowFocus = SwitchingWindowCalculated

/**
 * Switching window visualization data for chart
 */
export type SwitchingWindowCalculationResult = {
  switchingWindow: SwitchingWindowRead
  calculations: SwitchingWindowCalculation[]
}

/**
 * Units for each ResultFileType
 */
export const RESULT_FILE_TYPE_UNITS = {
  [ResultFileType.MaxAuslastungZweigeN1]: '%',
  [ResultFileType.AuslastungZweigeGrundfall]: '%',
  [ResultFileType.Wirkleistung]: 'MW',
  [ResultFileType.Blindleistung]: 'MVAr',
  [ResultFileType.EngpassleistungZweigeN1]: 'MW',
  [ResultFileType.EngpassleistungGrundfall]: 'MW',
  [ResultFileType.FlussrichtungZweigeGrundfall]: '',
  [ResultFileType.FlussrichtungZweigeN1]: '',
  [ResultFileType.SpannungsbetragKnotenGrundfall]: 'kV',
  [ResultFileType.MinSpannungKnotenN1]: 'kV',
  [ResultFileType.MaxSpannungKnotenN1]: 'kV',
  [ResultFileType.ScheinleistungGrundfall]: 'MVA',
  [ResultFileType.AustauschleistungNetzgruppenN0]: 'MW',
  [ResultFileType.Berechnung]: '',
  [ResultFileType.MaxAuslastungLeitungenN1]: '%',
  [ResultFileType.MaxAuslastungTransformatorenN1]: '%',
  [ResultFileType.NnfAuswertungCne]: '',
  [ResultFileType.NnfAuswertungContingency]: '',
  [ResultFileType.NnfUebersicht]: '',
} as const

export function getUnit(resultSource: ResultFileType): string {
  return RESULT_FILE_TYPE_UNITS[resultSource]
}

/**
 * Only the types that are actually used in timeline (first 12 entries)
 */
export type ResultFileTypesTimeline =
  | typeof ResultFileType.EngpassleistungGrundfall
  | typeof ResultFileType.EngpassleistungZweigeN1
  | typeof ResultFileType.MinSpannungKnotenN1
  | typeof ResultFileType.MaxSpannungKnotenN1
  | typeof ResultFileType.FlussrichtungZweigeN1
  | typeof ResultFileType.FlussrichtungZweigeGrundfall
  | typeof ResultFileType.SpannungsbetragKnotenGrundfall
  | typeof ResultFileType.Blindleistung
  | typeof ResultFileType.Wirkleistung
  | typeof ResultFileType.MaxAuslastungZweigeN1
  | typeof ResultFileType.AuslastungZweigeGrundfall
  | typeof ResultFileType.ScheinleistungGrundfall

/**
 * Configuration for a timeline data type
 */
export type TimelineResultConfig<T extends ResultFileTypesTimeline = ResultFileTypesTimeline> = {
  apiMethodName: Extract<MethodNames<ResultFileApi>, `download${string}Csv`>
  metadataApiMethodName: Extract<MethodNames<ResultFileApi>, `get${string}Metadata`>
  type: T
}

/**
 * Available timeline data types from ResultFileApi
 * Object keyed by ResultFileType for easy lookup
 */
export const TimelineResultsConfigMap: {
  [K in ResultFileTypesTimeline]: TimelineResultConfig<K>
} = {
  [ResultFileType.EngpassleistungGrundfall]: {
    apiMethodName: 'downloadEngpassleistungGrundfallCsv',
    metadataApiMethodName: 'getEngpassleistungGrundfallMetadata',
    type: ResultFileType.EngpassleistungGrundfall,
  },
  [ResultFileType.EngpassleistungZweigeN1]: {
    apiMethodName: 'downloadEngpassleistungZweigeN1Csv',
    metadataApiMethodName: 'getEngpassleistungZweigeN1Metadata',
    type: ResultFileType.EngpassleistungZweigeN1,
  },
  [ResultFileType.MinSpannungKnotenN1]: {
    apiMethodName: 'downloadMinSpannungKnotenN1Csv',
    metadataApiMethodName: 'getMinSpannungKnotenN1Metadata',
    type: ResultFileType.MinSpannungKnotenN1,
  },
  [ResultFileType.MaxSpannungKnotenN1]: {
    apiMethodName: 'downloadMaxSpannungKnotenN1Csv',
    metadataApiMethodName: 'getMaxSpannungKnotenN1Metadata',
    type: ResultFileType.MaxSpannungKnotenN1,
  },
  [ResultFileType.FlussrichtungZweigeN1]: {
    apiMethodName: 'downloadFlussrichtungZweigeN1Csv',
    metadataApiMethodName: 'getFlussrichtungZweigeN1Metadata',
    type: ResultFileType.FlussrichtungZweigeN1,
  },
  [ResultFileType.FlussrichtungZweigeGrundfall]: {
    apiMethodName: 'downloadFlussrichtungZweigeGrundfallCsv',
    metadataApiMethodName: 'getFlussrichtungZweigeGrundfallMetadata',
    type: ResultFileType.FlussrichtungZweigeGrundfall,
  },
  [ResultFileType.SpannungsbetragKnotenGrundfall]: {
    apiMethodName: 'downloadSpannungsbetragKnotenGrundfallCsv',
    metadataApiMethodName: 'getSpannungsbetragKnotenGrundfallMetadata',
    type: ResultFileType.SpannungsbetragKnotenGrundfall,
  },
  [ResultFileType.Blindleistung]: {
    apiMethodName: 'downloadBlindleistungCsv',
    metadataApiMethodName: 'getBlindleistungMetadata',
    type: ResultFileType.Blindleistung,
  },
  [ResultFileType.Wirkleistung]: {
    apiMethodName: 'downloadWirkleistungCsv',
    metadataApiMethodName: 'getWirkleistungMetadata',
    type: ResultFileType.Wirkleistung,
  },
  [ResultFileType.MaxAuslastungZweigeN1]: {
    apiMethodName: 'downloadMaxAuslastungZweigeN1Csv',
    metadataApiMethodName: 'getMaxAuslastungZweigeN1Metadata',
    type: ResultFileType.MaxAuslastungZweigeN1,
  },
  [ResultFileType.AuslastungZweigeGrundfall]: {
    apiMethodName: 'downloadAuslastungZweigeGrundfallCsv',
    metadataApiMethodName: 'getAuslastungZweigeGrundfallMetadata',
    type: ResultFileType.AuslastungZweigeGrundfall,
  },
  [ResultFileType.ScheinleistungGrundfall]: {
    apiMethodName: 'downloadScheinleistungGrundfallCsv',
    metadataApiMethodName: 'getScheinleistungGrundfallMetadata',
    type: ResultFileType.ScheinleistungGrundfall,
  },
} as const

export const ResultFileTypeTimeline = Object.keys(TimelineResultsConfigMap) as ResultFileTypesTimeline[]

/**
 * Columns which are fixed in a timeline CSV (should be the first three, then come equipment names)
 */
export enum TimelineCSVColumns {
  LZ = 'LZ',
  Sonderschaltung = 'Sonderschaltung',
  AnzKonv = 'Anz. n. konv.',
}

/**
 * Columns to exclude from equipment data
 */
export const EXCLUDE_COLUMNS: string[] = [
  TimelineCSVColumns.LZ,
  TimelineCSVColumns.Sonderschaltung,
  TimelineCSVColumns.AnzKonv,
]

/**
 * ECharts default color palette
 */
export const ECHARTS_COLORS = [
  '#5470c6',
  '#91cc75',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
  '#ea7ccc',
]

/**
 * Parsed timeline CSV payload.
 * Assumes first CSV row is header; empty lines are skipped by parser config.
 */
export type TimelineCsvData = {
  headers: string[]
  data: string[][]
}

export async function parseTimelineCsv(csvText: string): Promise<TimelineCsvData> {
  return await new Promise((resolve, reject) => {
    Papa.parse<string[]>(csvText, {
      worker: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          const firstError = results.errors[0]
          reject(new Error(`CSV parse error at row ${firstError.row}: ${firstError.message}`))
          return
        }

        const rows: string[][] = []
        for (const row of results.data) {
          if (!Array.isArray(row)) {
            continue
          }
          rows.push(row.map((cell) => `${cell ?? ''}`))
        }

        if (rows.length === 0) {
          resolve({ headers: [], data: [] })
          return
        }

        const [headers, ...data] = rows
        resolve({ headers, data })
      },
      error: (error: Error) => {
        reject(error)
      },
    })
  })
}

type TimelineColumnInfo = {
  xAxisIndex: number
  equipmentColumns: Array<{ index: number; name: string }>
}

function selectTimelineColumns(headers: string[], equipmentFilter: string[]): TimelineColumnInfo {
  let xAxisIndex: null | number = null
  const equipmentColumns: Array<{ index: number; name: string }> = []

  for (let i = 0; i < headers.length; i++) {
    const colName = headers[i]
    if (colName === TimelineCSVColumns.LZ) {
      xAxisIndex = i
      continue
    }
    if (EXCLUDE_COLUMNS.includes(colName)) {
      continue
    }
    if (equipmentFilter.includes(colName)) {
      equipmentColumns.push({ index: i, name: colName })
    }
  }

  if (xAxisIndex === null) {
    throw new Error(`${TimelineCSVColumns.LZ} column not found in data`)
  }

  return {
    xAxisIndex,
    equipmentColumns,
  }
}

function getXAxisData(rows: string[][], xAxisIndex: number): number[] {
  return rows.map((row) => parseFloat(row[xAxisIndex]) || 0)
}

function getEquipmentValues(rows: string[][], columnIndex: number): number[] {
  return rows.map((row) => parseFloat(row[columnIndex]) || 0)
}

/**
 * Transform API response to chart data structure
 */
export function fileToChartData(
  response: TimelineCsvData,
  result: ResultRead,
  viewMode: ViewModeEnum,
  colorOffset: number = 0,
  equipmentFilter: string[] = [],
): ResultChartData {
  const rows = response.data
  const { xAxisIndex, equipmentColumns } = selectTimelineColumns(response.headers, equipmentFilter)

  // Extract X-axis data (LZ hours)
  const xAxisData = getXAxisData(rows, xAxisIndex)
  const series: EquipmentSeries[] = []
  let seriesIndex = colorOffset

  // Calculate max value across all equipment for each time point
  if (viewMode === ViewModeEnum.max) {
    const maxValues = rows.map((row) => {
      const equipmentValues = equipmentColumns.map((col) => parseFloat(row[col.index]) || 0)
      return Math.max(...equipmentValues)
    })

    const seriesName = `${result.topology_name} - Max`
    series.push({
      name: seriesName,
      uniqueId: `${result.id}_${seriesName}`,
      data: maxValues,
      color: ECHARTS_COLORS[seriesIndex % ECHARTS_COLORS.length],
      visible: true,
    })
    seriesIndex++
  }

  // Create series for each equipment, grouped by result
  if (viewMode === ViewModeEnum.individual) {
    for (const equipmentCol of equipmentColumns) {
      // Format: [Result] Equipment (better grouping in legend)
      const seriesName = `[${result.topology_name}] ${equipmentCol.name}`
      series.push({
        name: seriesName,
        uniqueId: `${result.id}_${seriesName}`,
        data: getEquipmentValues(rows, equipmentCol.index),
        color: ECHARTS_COLORS[seriesIndex % ECHARTS_COLORS.length],
        visible: true,
      })
      seriesIndex++
    }
  }

  return {
    result,
    xAxisData,
    series,
  }
}

/**
 * Export chart data to CSV format
 */
export function exportToCSV(chartData: ResultChartData[], filename: string): void {
  const rows: string[][] = []

  // Header row
  const headerRow: string[] = [TimelineCSVColumns.LZ]
  for (const data of chartData) {
    for (const equipmentSeries of data.series) {
      headerRow.push(equipmentSeries.name)
    }
  }
  rows.push(headerRow)

  // Data rows
  const maxLength = Math.max(...chartData.map((d) => d.xAxisData.length))
  for (let i = 0; i < maxLength; i++) {
    const row = [chartData[0].xAxisData[i].toString()]
    for (const data of chartData) {
      for (const series of data.series) {
        row.push(series.data[i]?.toString() || '')
      }
    }
    rows.push(row)
  }

  // Generate CSV and download
  const csv = rows.map((row) => row.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
